import React from "react";
import PropTypes from "prop-types";
import { withFormik } from "formik";
import Yup from "yup";
import renderHTML from "react-render-html";
import StepsHeader from "../Global/Misc/StepsHeader";
import GradHero from "../Global/Heros/GradHero";
import MaxWidthContainer from "../Global/Containers/MaxWidthContainer";
import LazyLoad from "../Global/Loaders/LazyLoad";
import SpinLoader from "../Global/Loaders/SpinLoader";
import SignupTable from "./SignupTable";
import SignupDetails from "./SignupDetails";
import SignupSummary from "./SignupSummary";
import Form from "../Global/Forms/Form";

const SignupForm = ({
  // Props passed from SignupPage
  signup,
  stripe,
  activePage,
  heroText,
  signupOptionsLoading,
  onBackClick,
  onContinueClick,
  acceptedTerms,
  onAcceptTermsChange,
  onChangePageClick,
  onApplyDiscountClick,
  applyDiscountLoading,
  submitting,
  activePlanType,
  activeBillingFrequency,
  takingTheirTime,
  currentPage,
  ageCategories,
  // Formik props
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  submitForm,
  isValid,
}) => {
  const handlePrimaryRoleChange = (e) => {
    // Because isCoach and isPlayer are seperate keys in the signup object, we need to manaully toggle the values when the radio inputs are changed
    // (if it was a single key such as 'playerOrCoach' and a single value then we wouldn't have to do this)
    if (e.target.name === `isPlayer`) {
      // setFieldValue is a Formik specific prop
      setFieldValue(`isCoach`, false);
      setFieldValue(`isPlayer`, true);
    } else {
      setFieldValue(`isCoach`, true);
      setFieldValue(`isPlayer`, false);
    }
  };

  // Because when changing the country code we need to set 2 fields we need to do it manually
  const handleCountryPhoneCodeChange = (e, dialCode) => {
    setFieldValue(`areaNo1`, dialCode);
    setFieldValue(`areaCode1`, e.target.value);
  };

  const handleContinueClick = (e) => {
    e.preventDefault();
    switch (activePage) {
      case 1:
        if (values.planType === `enterprise`) {
          window.location = `https://content.thecoachingmanual.com/enterprise-trial`;
        } else {
          onContinueClick();
        }
        break;
      case 2:
        if (isValid) {
          onContinueClick();
        } else {
          // Formik form submission to trigger error messages
          submitForm();
        }
        break;
      // no default
    }
  };

  const handlePlanClick = (e) => {
    // Little UX improvement to allow people to click on an active plan type radio button to go through to the next page
    if (e.target.value === values.planType) {
      handleContinueClick(e);
    } else {
      handleChange(e);
    }
  };

  const handleApplyDiscountClick = (e) => {
    e.preventDefault();
    onApplyDiscountClick(values.discountCode);
  };

  let signupTable;
  if (signupOptionsLoading) {
    signupTable = (
      <LazyLoad>
        <SpinLoader black />
      </LazyLoad>
    );
  } else {
    signupTable = (
      <div className="m-b-7">
        <SignupTable
          signup={signup}
          values={values}
          onChange={handleChange}
          onPlanClick={
            currentPage !== `/payment-plans` ? handlePlanClick : undefined
          }
        />
      </div>
    );
  }

  const formContainerClass = `signup-form m-b-5 signup-form--${values.planType}-selected signup-form--step-${activePage}`;
  const totalSteps = 3;

  return (
    <Form className={formContainerClass}>
      {currentPage !== `/payment-plans` && (
        <StepsHeader
          onBackClick={onBackClick}
          activeStep={activePage}
          totalSteps={totalSteps}
          onNextClick={handleContinueClick}
          pulseNextButton={
            activePage === 1 &&
            (activePlanType !== values.planType ||
              activeBillingFrequency !== values.billingFrequency ||
              takingTheirTime)
          }
        />
      )}

      <div className="m-b-5">
        <GradHero>
          <h2 className="like-h1 m-b-0">{renderHTML(heroText)}</h2>
        </GradHero>
      </div>

      <MaxWidthContainer>
        {activePage === 1 && signupTable}

        {activePage === 2 && (
          <SignupDetails
            stripe={stripe}
            values={values}
            errors={errors}
            touched={touched}
            onChange={handleChange}
            onPrimaryRoleChange={handlePrimaryRoleChange}
            onCountryPhoneCodeChange={handleCountryPhoneCodeChange}
            onApplyDiscountClick={handleApplyDiscountClick}
            applyDiscountLoading={applyDiscountLoading}
            ageCategories={ageCategories}
          />
        )}

        {activePage === 3 && (
          <SignupSummary
            signup={signup}
            stripe={stripe}
            values={values}
            onChange={handleChange}
            acceptedTerms={acceptedTerms}
            onAcceptTermsChange={onAcceptTermsChange}
            onChangePageClick={onChangePageClick}
            isSubmitting={submitting}
          />
        )}
      </MaxWidthContainer>
    </Form>
  );
};

SignupForm.propTypes = {
  signup: PropTypes.object.isRequired,
  stripe: PropTypes.object,
  activePage: PropTypes.number.isRequired,
  heroText: PropTypes.string.isRequired,
  signupOptionsLoading: PropTypes.bool.isRequired,
  onBackClick: PropTypes.func.isRequired,
  onContinueClick: PropTypes.func.isRequired,
  acceptedTerms: PropTypes.bool.isRequired,
  onAcceptTermsChange: PropTypes.func.isRequired,
  onChangePageClick: PropTypes.func.isRequired,
  onApplyDiscountClick: PropTypes.func.isRequired,
  applyDiscountLoading: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  activePlanType: PropTypes.string.isRequired,
  activeBillingFrequency: PropTypes.string.isRequired,
  takingTheirTime: PropTypes.bool,
  currentPage: PropTypes.string.isRequired,
  ageCategories: PropTypes.array,
  // Formik
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  isValid: PropTypes.bool.isRequired,
  touched: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  submitForm: PropTypes.func.isRequired,
};

const FormikSignupForm = withFormik({
  enableReinitialize: true,
  mapPropsToValues({ data, activePlanType, activeBillingFrequency }) {
    const {
      planType,
      billingFrequency,
      firstName,
      lastName,
      emailAddress,
      isCoach,
      isPlayer,
      coachAgeGroup,
      playerAgeGroup,
      areaNo1,
      areaCode1,
      telNo1,
      cardNumber,
      nameOnCard,
      expiryMonth,
      expiryYear,
      billingLine1,
      billingLine2,
      billingCity,
      billingState,
      billingZip,
      billingCountry,
      discountCode,
      newsletters,
    } = data;

    return {
      planType: planType || activePlanType,
      billingFrequency: billingFrequency || activeBillingFrequency,
      firstName: firstName || ``,
      lastName: lastName || ``,
      emailAddress: emailAddress || ``,
      confirmEmailAddress: emailAddress || ``,
      isCoach: typeof isCoach === `boolean` ? isCoach : true,
      isPlayer: typeof isPlayer === `boolean` ? isPlayer : false,
      coachAgeGroup: coachAgeGroup || [],
      playerAgeGroup: playerAgeGroup || [],
      areaNo1: areaNo1 || ``,
      areaCode1: areaCode1 || ``,
      telNo1: telNo1 || ``,
      password: ``,
      confirmPassword: ``,
      cardNumber: cardNumber || ``,
      cvc: ``,
      nameOnCard: nameOnCard || ``,
      expiryMonth: expiryMonth || ``,
      expiryYear: expiryYear || ``,
      billingLine1: billingLine1 || ``,
      billingLine2: billingLine2 || ``,
      billingCity: billingCity || ``,
      billingState: billingState || ``,
      billingZip: billingZip || ``,
      billingCountry: billingCountry || ``,
      discountCode: discountCode || ``,
      newsletters: typeof newsletters === `boolean` ? newsletters : false,
    };
  },
  validationSchema: () =>
    Yup.lazy(() =>
      Yup.object().shape({
        firstName: Yup.string()
          .label(`First name`)
          .required(),
        lastName: Yup.string()
          .label(`Last name`)
          .required(),
        emailAddress: Yup.string()
          .label(`Email address`)
          .email()
          .matches(
            /^[A-Za-z0-9.+_-]+@[A-Za-z0-9._-]+\.[a-zA-Z]*$/,
            `Must not contain international characters`,
          )
          .required(),
        confirmEmailAddress: Yup.string()
          .label(`Confirm email address`)
          .oneOf([Yup.ref(`emailAddress`)], `Must match your email address`)
          .required(),
        areaCode1: Yup.string()
          .label(`Area code`)
          .required(),
        telNo1: Yup.string()
          .label(`Phone number`)
          .required(),
        password: Yup.string()
          .label(`Password`)
          .min(8, `Must be at least 8 characters`)
          .matches(/[A-Z]/, `Must contain an uppercase letter`)
          .matches(/[a-z]/, `Must contain a lowercase letter`)
          .matches(/[0-9]/, `Must contain a number`)
          .required(),
        confirmPassword: Yup.string()
          .label(`Confirm password`)
          .oneOf([Yup.ref(`password`)], `Must match your password`)
          .required(),
      }),
    ),
  handleSubmit(values, bag) {
    bag.props.onSubmit(values);
  },
})(SignupForm);

export default FormikSignupForm;



// WEBPACK FOOTER //
// ./library/app/components/Signup/SignupForm.js